import {useEffect, useState} from 'react';
import logger from '../../services/logger';

export interface AudioDeviceLists {
    readonly inputs: readonly MediaDeviceInfo[];
    readonly outputs: readonly MediaDeviceInfo[];
    readonly blocked: boolean;
}

const EMPTY: AudioDeviceLists = {inputs: [], outputs: [], blocked: false};

const listDevices = async (): Promise<AudioDeviceLists> => {
    let devices = await navigator.mediaDevices.enumerateDevices();
    let blocked = false;
    if (devices.some(device => device.kind === 'audioinput' && !device.label)) {
        try {
            const stream = await navigator.mediaDevices.getUserMedia({audio: true});
            stream.getTracks().forEach(track => track.stop());
            devices = await navigator.mediaDevices.enumerateDevices();
        } catch (error) {
            blocked = (error as Error)?.name === 'NotAllowedError';
            logger.warn('Microphone permission not granted for device listing', {
                source: 'audio_settings',
                event: 'device_label_permission_failed',
                errorName: (error as Error)?.name,
            });
        }
    }
    return {
        inputs: devices.filter(device => device.kind === 'audioinput' && device.deviceId),
        outputs: devices.filter(device => device.kind === 'audiooutput' && device.deviceId),
        blocked,
    };
};

export const useAudioDevices = (): AudioDeviceLists => {
    const [lists, setLists] = useState<AudioDeviceLists>(EMPTY);
    useEffect(() => {
        let latest = 0;
        let cancelled = false;
        const reload = () => {
            const request = ++latest;
            listDevices()
                .then(next => {
                    if (!cancelled && request === latest) {
                        setLists(next);
                    }
                })
                .catch(error => logger.warn('Failed to list audio devices', {
                    source: 'audio_settings',
                    event: 'device_list_failed',
                    errorName: (error as Error)?.name,
                }));
        };
        reload();
        navigator.mediaDevices.addEventListener('devicechange', reload);
        return () => {
            cancelled = true;
            navigator.mediaDevices.removeEventListener('devicechange', reload);
        };
    }, []);
    return lists;
};
